import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Calculator, DollarSign, Users, Calendar, FileText, AlertCircle, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('es-GT', {
    style: 'currency',
    currency: 'GTQ',
    minimumFractionDigits: 2
  }).format(amount || 0);
};

const Calculadora = () => {
  const [tasas, setTasas] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Estado para cada calculadora
  const [salarioPlanilla, setSalarioPlanilla] = useState('');
  const [resultadoPlanilla, setResultadoPlanilla] = useState(null);
  
  const [salarioISR, setSalarioISR] = useState('');
  const [resultadoISR, setResultadoISR] = useState(null);
  
  const [datosLiquidacion, setDatosLiquidacion] = useState({
    salario: '',
    meses_bono14: 12,
    meses_aguinaldo: 12,
    dias_vacaciones: 0,
    anos_trabajados: 0,
    despido_injustificado: false
  });
  const [resultadoLiquidacion, setResultadoLiquidacion] = useState(null);

  // Cargar tasas vigentes al montar
  useEffect(() => {
    const fetchTasas = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/calculos/tasas`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTasas(response.data);
      } catch (error) {
        toast.error('Error al cargar tasas vigentes');
      }
    };
    fetchTasas();
  }, []);

  const calcularPlanilla = async () => {
    if (!salarioPlanilla || parseFloat(salarioPlanilla) <= 0) {
      toast.error('Ingresa un salario válido');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/calculos/planilla`, null, {
        params: { salario_bruto: parseFloat(salarioPlanilla) },
        headers: { Authorization: `Bearer ${token}` }
      });
      setResultadoPlanilla(response.data);
    } catch (error) {
      toast.error('Error al calcular planilla');
    } finally {
      setLoading(false);
    }
  };

  const calcularISR = async () => {
    if (!salarioISR || parseFloat(salarioISR) <= 0) {
      toast.error('Ingresa un salario válido');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/calculos/isr`, null, {
        params: { salario_mensual: parseFloat(salarioISR) },
        headers: { Authorization: `Bearer ${token}` }
      });
      setResultadoISR(response.data);
    } catch (error) {
      toast.error('Error al calcular ISR');
    } finally {
      setLoading(false);
    }
  };

  const calcularLiquidacion = async () => {
    if (!datosLiquidacion.salario || parseFloat(datosLiquidacion.salario) <= 0) {
      toast.error('Ingresa un salario válido');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/calculos/liquidacion`, null, {
        params: {
          salario_mensual: parseFloat(datosLiquidacion.salario),
          meses_bono14: datosLiquidacion.meses_bono14,
          meses_aguinaldo: datosLiquidacion.meses_aguinaldo,
          dias_vacaciones_pendientes: datosLiquidacion.dias_vacaciones,
          anos_trabajados: datosLiquidacion.anos_trabajados,
          despido_injustificado: datosLiquidacion.despido_injustificado
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      setResultadoLiquidacion(response.data);
    } catch (error) {
      toast.error('Error al calcular liquidación');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="calculadora-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Calculadora Laboral</h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">Cálculos de Ley Guatemala - IGSS, ISR, Bono 14, Aguinaldo</p>
        </div>
      </div>

      {/* Tasas Vigentes */}
      {tasas && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Users className="h-4 w-4" />
                IGSS Laboral
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">{tasas.igss.laboral}%</p>
              <p className="text-xs text-blue-100 mt-1">Descuento al trabajador</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                IGSS Patronal
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">{tasas.igss.patronal}%</p>
              <p className="text-xs text-purple-100 mt-1">Incluye IRTRA (1%) + INTECAP (1%)</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white border-0">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Info className="h-4 w-4" />
                Mínimo Vital Anual
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">Q 48,000</p>
              <p className="text-xs text-green-100 mt-1">Exento de ISR</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs de Calculadoras */}
      <Tabs defaultValue="planilla" className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="planilla" data-testid="planilla-tab">
            <Calculator className="h-4 w-4 mr-2" />
            Planilla Mensual
          </TabsTrigger>
          <TabsTrigger value="isr" data-testid="isr-tab">
            <FileText className="h-4 w-4 mr-2" />
            Cálculo ISR
          </TabsTrigger>
          <TabsTrigger value="liquidacion" data-testid="liquidacion-tab">
            <DollarSign className="h-4 w-4 mr-2" />
            Liquidación
          </TabsTrigger>
        </TabsList>

        {/* Tab: Planilla Mensual */}
        <TabsContent value="planilla" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calculator className="h-5 w-5 text-blue-600" />
                Calculadora de Planilla Mensual
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-4 items-end">
                <div className="flex-1">
                  <Label htmlFor="salario-planilla">Salario Bruto Mensual (Q)</Label>
                  <Input
                    id="salario-planilla"
                    type="number"
                    value={salarioPlanilla}
                    onChange={(e) => setSalarioPlanilla(e.target.value)}
                    placeholder="Ej: 8000"
                    className="mt-1"
                    data-testid="salario-planilla-input"
                  />
                </div>
                <Button 
                  onClick={calcularPlanilla} 
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Calculator className="h-4 w-4 mr-2" />
                  Calcular
                </Button>
              </div>
              
              {resultadoPlanilla && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                  {/* Descuentos al Trabajador */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-slate-900 dark:text-white border-b pb-2">
                      Descuentos al Trabajador
                    </h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-slate-600 dark:text-slate-400">Salario Bruto:</span>
                        <span className="font-mono font-semibold">{formatCurrency(resultadoPlanilla.salario_bruto)}</span>
                      </div>
                      <div className="flex justify-between text-red-600">
                        <span>(-) IGSS Laboral (4.83%):</span>
                        <span className="font-mono">{formatCurrency(resultadoPlanilla.descuentos.igss_laboral)}</span>
                      </div>
                      <div className="flex justify-between text-red-600">
                        <span>(-) ISR:</span>
                        <span className="font-mono">{formatCurrency(resultadoPlanilla.descuentos.isr)}</span>
                      </div>
                      <div className="flex justify-between font-bold border-t pt-2 text-green-600">
                        <span>Salario Neto:</span>
                        <span className="font-mono">{formatCurrency(resultadoPlanilla.salario_neto)}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Costo Total Empleador */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-slate-900 dark:text-white border-b pb-2">
                      Costo Total Empleador
                    </h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-slate-600 dark:text-slate-400">Salario Bruto:</span>
                        <span className="font-mono">{formatCurrency(resultadoPlanilla.costo_empleador.salario_bruto)}</span>
                      </div>
                      <div className="flex justify-between text-purple-600">
                        <span>(+) IGSS Patronal (12.67%):</span>
                        <span className="font-mono">{formatCurrency(resultadoPlanilla.costo_empleador.igss_patronal)}</span>
                      </div>
                      <div className="flex justify-between text-amber-600">
                        <span>(+) Provisión Bono 14:</span>
                        <span className="font-mono">{formatCurrency(resultadoPlanilla.costo_empleador.provision_bono14)}</span>
                      </div>
                      <div className="flex justify-between text-amber-600">
                        <span>(+) Provisión Aguinaldo:</span>
                        <span className="font-mono">{formatCurrency(resultadoPlanilla.costo_empleador.provision_aguinaldo)}</span>
                      </div>
                      <div className="flex justify-between text-amber-600">
                        <span>(+) Provisión Vacaciones:</span>
                        <span className="font-mono">{formatCurrency(resultadoPlanilla.costo_empleador.provision_vacaciones)}</span>
                      </div>
                      <div className="flex justify-between font-bold border-t pt-2 text-blue-700">
                        <span>Costo Total:</span>
                        <span className="font-mono">{formatCurrency(resultadoPlanilla.costo_empleador.costo_total)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Cálculo ISR */}
        <TabsContent value="isr" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-green-600" />
                Calculadora de ISR (Impuesto Sobre la Renta)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-slate-50 dark:bg-slate-800 p-4 rounded-lg">
                <h5 className="font-semibold mb-2">Tabla de ISR Guatemala</h5>
                <ul className="text-sm space-y-1 text-slate-600 dark:text-slate-400">
                  <li>• Renta imponible Q0 - Q300,000: <strong>5%</strong></li>
                  <li>• Renta imponible mayor a Q300,000: <strong>7% + Q15,000 fijo</strong></li>
                  <li>• Mínimo vital exento: <strong>Q48,000 anuales</strong></li>
                </ul>
              </div>
              
              <div className="flex gap-4 items-end">
                <div className="flex-1">
                  <Label htmlFor="salario-isr">Salario Mensual (Q)</Label>
                  <Input
                    id="salario-isr"
                    type="number"
                    value={salarioISR}
                    onChange={(e) => setSalarioISR(e.target.value)}
                    placeholder="Ej: 15000"
                    className="mt-1"
                    data-testid="salario-isr-input"
                  />
                </div>
                <Button 
                  onClick={calcularISR} 
                  disabled={loading}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Calculator className="h-4 w-4 mr-2" />
                  Calcular ISR
                </Button>
              </div>
              
              {resultadoISR && (
                <div className="bg-white dark:bg-slate-800 border rounded-lg p-4 mt-4">
                  <h4 className="font-semibold mb-3">Resultado del Cálculo ISR</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-500">Renta Anual Proyectada (14 salarios)</p>
                      <p className="font-mono text-lg">{formatCurrency(resultadoISR.renta_anual_proyectada)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Mínimo Vital (Exento)</p>
                      <p className="font-mono text-lg">{formatCurrency(resultadoISR.minimo_vital)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Renta Imponible</p>
                      <p className="font-mono text-lg">{formatCurrency(resultadoISR.renta_imponible)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">ISR Anual</p>
                      <p className="font-mono text-lg">{formatCurrency(resultadoISR.isr_anual)}</p>
                    </div>
                    <div className="col-span-2 bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
                      <p className="text-sm text-green-700 dark:text-green-400">ISR Mensual a Retener</p>
                      <p className="font-mono text-2xl font-bold text-green-700 dark:text-green-400">
                        {formatCurrency(resultadoISR.isr_mensual)}
                      </p>
                      <p className="text-xs text-green-600 dark:text-green-500">
                        Tasa efectiva: {resultadoISR.tasa_efectiva_porcentaje}%
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Liquidación */}
        <TabsContent value="liquidacion" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-amber-600" />
                Calculadora de Liquidación Laboral
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label>Salario Mensual (Q)</Label>
                  <Input
                    type="number"
                    value={datosLiquidacion.salario}
                    onChange={(e) => setDatosLiquidacion({...datosLiquidacion, salario: e.target.value})}
                    placeholder="Ej: 8000"
                    className="mt-1"
                    data-testid="liquidacion-salario-input"
                  />
                </div>
                <div>
                  <Label>Meses Bono 14</Label>
                  <Input
                    type="number"
                    min="0"
                    max="12"
                    value={datosLiquidacion.meses_bono14}
                    onChange={(e) => setDatosLiquidacion({...datosLiquidacion, meses_bono14: parseInt(e.target.value) || 0})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Meses Aguinaldo</Label>
                  <Input
                    type="number"
                    min="0"
                    max="12"
                    value={datosLiquidacion.meses_aguinaldo}
                    onChange={(e) => setDatosLiquidacion({...datosLiquidacion, meses_aguinaldo: parseInt(e.target.value) || 0})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Días Vacaciones Pendientes</Label>
                  <Input
                    type="number"
                    min="0"
                    value={datosLiquidacion.dias_vacaciones}
                    onChange={(e) => setDatosLiquidacion({...datosLiquidacion, dias_vacaciones: parseInt(e.target.value) || 0})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Años Trabajados</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.5"
                    value={datosLiquidacion.anos_trabajados}
                    onChange={(e) => setDatosLiquidacion({...datosLiquidacion, anos_trabajados: parseFloat(e.target.value) || 0})}
                    className="mt-1"
                  />
                </div>
                <div className="flex items-end">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="despido"
                      checked={datosLiquidacion.despido_injustificado}
                      onCheckedChange={(checked) => setDatosLiquidacion({...datosLiquidacion, despido_injustificado: checked})}
                    />
                    <Label htmlFor="despido" className="text-sm">
                      Despido Injustificado (Indemnización)
                    </Label>
                  </div>
                </div>
              </div>
              
              <Button 
                onClick={calcularLiquidacion} 
                disabled={loading}
                className="bg-amber-600 hover:bg-amber-700 w-full"
              >
                <Calculator className="h-4 w-4 mr-2" />
                Calcular Liquidación
              </Button>
              
              {resultadoLiquidacion && (
                <div className="bg-white dark:bg-slate-800 border rounded-lg p-4 mt-4">
                  <h4 className="font-semibold mb-3">Resultado de la Liquidación</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between py-2 border-b">
                      <span>Bono 14 ({resultadoLiquidacion.bono_14.meses_trabajados} meses):</span>
                      <span className="font-mono font-semibold">{formatCurrency(resultadoLiquidacion.bono_14.monto)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span>Aguinaldo ({resultadoLiquidacion.aguinaldo.meses_trabajados} meses):</span>
                      <span className="font-mono font-semibold">{formatCurrency(resultadoLiquidacion.aguinaldo.monto)}</span>
                    </div>
                    {resultadoLiquidacion.vacaciones && (
                      <div className="flex justify-between py-2 border-b">
                        <span>Vacaciones ({resultadoLiquidacion.vacaciones.dias_vacaciones} días):</span>
                        <span className="font-mono font-semibold">{formatCurrency(resultadoLiquidacion.vacaciones.monto)}</span>
                      </div>
                    )}
                    {resultadoLiquidacion.indemnizacion && (
                      <div className="flex justify-between py-2 border-b text-red-600">
                        <span>Indemnización ({resultadoLiquidacion.indemnizacion.anos_trabajados} años):</span>
                        <span className="font-mono font-semibold">{formatCurrency(resultadoLiquidacion.indemnizacion.monto)}</span>
                      </div>
                    )}
                    <div className="flex justify-between py-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg px-3">
                      <span className="font-bold text-amber-800 dark:text-amber-400">TOTAL LIQUIDACIÓN:</span>
                      <span className="font-mono text-xl font-bold text-amber-800 dark:text-amber-400">
                        {formatCurrency(resultadoLiquidacion.total_liquidacion)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Nota Legal */}
      <Card className="bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
        <CardContent className="pt-4">
          <div className="flex gap-3">
            <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-slate-600 dark:text-slate-400">
              <p className="font-semibold text-slate-800 dark:text-slate-200 mb-1">Nota Legal</p>
              <p>
                Estos cálculos son referenciales y están basados en la legislación laboral vigente de Guatemala 2024-2026.
                Para casos específicos, consulte con un profesional en derecho laboral o contabilidad.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Calculadora;
